/**
 * @singular-health/chatbase
 *
 * Production-ready wrapper for the Chatbase embed widget.
 * Provides bootstrap, widget control, event management, i18n, and
 * client-side custom actions through a typed TypeScript API.
 */

// Re-export public types
export type {
  ChatbaseConfig,
  ChatbaseEvent,
  ChatbaseEventHandler,
  ChatbaseEventName,
  ChatbaseUser,
  ChatbaseUserIdentity,
  CustomActionHandler,
  CustomActionMap,
  CustomActionResponse,
  TranslationMap,
} from "./types";

import type {
  ChatbaseConfig,
  ChatbaseEventHandler,
  ChatbaseEventName,
  CustomActionMap,
  TranslationMap,
} from "./types";

import { injectScript, waitForReady, removeWidget } from "./bootstrap/loader";
import {
  openChat as _openChat,
  closeChat as _closeChat,
  resetChat as _resetChat,
  setInitialMessages as _setInitialMessages,
  identify as _identify,
  resetUser as _resetUser,
  isWidgetInitialized,
} from "./api/chatbase-api";
import {
  DEFAULT_TRANSLATIONS,
  detectBrowserLanguage,
  getLanguageName,
  resolveMessages,
  t as _t,
} from "./i18n";
import { EventManager } from "./events/event-manager";
import { registerCustomActions } from "./actions/action-registry";
import { installOverlay, removeOverlay } from "./overlay/widget-overlay";

// ---------------------------------------------------------------------------
// Module-level singleton state
// ---------------------------------------------------------------------------

let _config: ChatbaseConfig | null = null;
let _currentLanguage: string = "en";
let _eventManager: EventManager | null = null;
let _initPromise: Promise<void> | null = null;

function debug(...args: unknown[]): void {
  if (_config?.debug) console.debug("[chatbase]", ...args);
}

function getTranslations(): TranslationMap {
  const custom = _config?.translations ?? {};
  const allLangs = new Set([...Object.keys(DEFAULT_TRANSLATIONS), ...Object.keys(custom)]);
  const merged: TranslationMap = {};
  for (const lang of allLangs) {
    merged[lang] = { ...(DEFAULT_TRANSLATIONS[lang] ?? {}), ...(custom[lang] ?? {}) };
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise the Chatbase widget.
 *
 * - Injects the Chatbase embed `<script>` and waits for the widget to
 *   report itself as initialised.
 * - Sets initial messages based on detected/configured language.
 * - Registers event handlers and custom actions provided in config.
 *
 * Safe to call multiple times — subsequent calls return the same promise.
 */
export function initChatbase(config: ChatbaseConfig): Promise<void> {
  if (_initPromise) {
    debug("initChatbase() called again — returning existing promise.");
    return _initPromise;
  }

  _config = config;
  _currentLanguage = config.language ?? detectBrowserLanguage();
  _eventManager = new EventManager();

  debug("Initialising", { botId: config.botId, language: _currentLanguage });

  // If autoLoad is explicitly false, resolve immediately without injecting
  if (config.autoLoad === false) {
    debug("autoLoad disabled — script not injected.");
    _initPromise = Promise.resolve();
    return _initPromise;
  }

  const style = document.createElement("style");
  style.innerHTML = `
    #chatbase-bubble-window {
      position: absolute !important;
      left: -999em !important;
    }
      #chatbase-bubble-button {
      position: absolute !important;
      left: -999em !important;
    }
  `;
  document.head.appendChild(style);
  const removeStyle = () => {
    setTimeout(() => {
      if (style.parentNode) style.remove();
      resetChat();

      let bubbleFound = 0;
      const interval = setInterval(() => {
        const win = document.getElementById("chatbase-bubble-window");
        const iframe = win?.getElementsByTagName("iframe")[0];

        if (bubbleFound > 5) {
          clearInterval(interval);
        } else if (win && iframe && win.style.display !== "none") {
          bubbleFound++;
          iframe.style.border = "none";
        } else if (win && iframe) {
          iframe.style.border = "1px solid #ccc";
        }
      }, 100);
    }, 100);
  };
  const doLoad = async () => {
    await injectScript({
      botId: config.botId,
      domain: config.domain,
      scriptUrl: config.scriptUrl,
    });
    debug("Embed script loaded — waiting for widget readiness…");
    await waitForReady();
    debug("Widget ready.");

    // Set initial messages
    const translations = getTranslations();
    const messages = config.initialMessages ?? resolveMessages(_currentLanguage, translations);
    _setInitialMessages(messages);

    // Event handlers from config
    if (config.eventHandlers) {
      for (const [event, handler] of Object.entries(config.eventHandlers)) {
        if (handler) _eventManager!.on(event, handler);
      }
    }

    // Custom actions from config
    if (config.customActions) {
      registerCustomActions(config.customActions, config.debug);
    }

    // Identity if provided
    if (config.user?.token || config.user?.userHash) {
      _identify({
        ...(config.user.userId ? { user_id: config.user.userId } : {}),
        ...(config.user.token ? { token: config.user.token } : {}),
        ...(config.user.userHash ? { user_hash: config.user.userHash } : {}),
        ...(config.user.metadata ?? {}),
      });
    }

    // Install translated widget overlay for non-English languages
    installOverlay({
      language: _currentLanguage,
      translations: getTranslations(),
      botId: config.botId,
      domain: config.domain,
      onResetChat: () => _resetChat(),
    });
    resetChat();
  };

  // If DOM isn't ready yet, defer injection
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    _initPromise = new Promise<void>((resolve, reject) => {
      window.addEventListener(
        "load",
        () => {
          doLoad()
            .then(() => {
              removeStyle();
            })
            .then(resolve, reject);
        },
        { once: true },
      );
    });
  } else {
    _initPromise = doLoad().then(() => {
      removeStyle();
    });
  }

  return _initPromise;
}

/** Open the chat widget. */
export function openChat(): void {
  _openChat();
}

/** Close the chat widget. */
export function closeChat(): void {
  _closeChat();
}

/** Reset the chat session. */
export function resetChat(): void {
  _resetChat();
}

/**
 * Change the widget language at runtime.
 *
 * Updates the initial greeting messages to match the new language and
 * resets the chat session so the translated greetings are displayed
 * inside the iframe.
 */
export function setLanguage(languageCode: string): void {
  if (!_config) {
    throw new Error("Cannot setLanguage before initChatbase() has been called.");
  }

  _currentLanguage = languageCode;
  debug("setLanguage", languageCode);

  // Update greeting messages for the new language
  const translations = getTranslations();
  const messages = resolveMessages(languageCode, translations);
  // messages.push(`Speak to me in ${getLanguageName(languageCode)}`);
  _setInitialMessages(messages);

  // Reset so the iframe reloads with the new greetings
  _resetChat();

  // Update or install the translated menu overlay
  installOverlay({
    language: languageCode,
    translations,
    botId: _config.botId,
    domain: _config.domain,
    onResetChat: () => _resetChat(),
  });
}

/** Set (or replace) the initial messages shown in the widget. */
export function setInitialMessages(messages: string[]): void {
  _setInitialMessages(messages);
}

/**
 * Look up a single translation key for the current (or specified) language.
 *
 * Falls back through: requested locale → "en" → key itself.
 */
export function t(key: string, language?: string): string {
  return _t(key, language ?? _currentLanguage, getTranslations());
}

/** Register a single event listener. Returns an unsubscribe function. */
export function on(event: ChatbaseEventName, handler: ChatbaseEventHandler): () => void {
  if (!_eventManager) _eventManager = new EventManager();
  _eventManager.on(event, handler);
  return () => _eventManager!.off(event, handler);
}

/** Unsubscribe a previously registered handler. */
export function off(event: ChatbaseEventName, handler: ChatbaseEventHandler): void {
  _eventManager?.off(event, handler);
}

/** Register client-side custom actions. */
export function registerClientActions(actions: CustomActionMap): void {
  registerCustomActions(actions, _config?.debug ?? false);
}

/** Identify the current user (identity verification). */
export function identifyUser(payload: Record<string, unknown>): void {
  _identify(payload);
}

/** Clear the current user's identity from the widget session. */
export function resetUser(): void {
  _resetUser();
}

/** Returns `true` once the widget has fully initialised. */
export function isInitialized(): boolean {
  return isWidgetInitialized();
}

/**
 * Tear down: close the widget, remove all managed event listeners,
 * remove the embed script and widget iframe from the DOM, and clear
 * internal state. After calling `destroy()`, `initChatbase()` can be
 * called again for a fresh setup.
 */
export function destroy(): void {
  debug("destroy()");
  _closeChat();
  _eventManager?.removeAll();
  _eventManager = null;
  removeOverlay();
  removeWidget();
  _config = null;
  _initPromise = null;
}
